/**
 * Detección de entorno — SSOT de "dónde está corriendo el promotor".
 *
 * El onboarding de Golden Parce tiene que adaptarse al navegador/plataforma real
 * porque cada combinación instala (o NO instala) de forma distinta. Este módulo
 * concentra TODA la heurística de detección, verificada contra la matriz de edge
 * cases (memoria: edges-pwa-instalacion-clipboard). Una sola forma de preguntar
 * "¿es iOS?", "¿es webview de WhatsApp?", "¿ya está instalada?".
 *
 * Regla de oro de la matriz: el user agent es spoofeable y hay falsos negativos
 * (webviews nuevos sin marca). Por eso quien consume esto SIEMPRE debe tener un
 * fallback a la guía manual aunque la detección falle.
 */

// esIOS/esStandalone son SSOT en shared/services/push-opt-in.js (incluyen el caso
// iPad-como-Mac). Los reexportamos para que este módulo siga siendo el único punto
// de detección de entorno del promotor, sin duplicar la heurística.
import { esIOS, esStandalone } from '@shared/services/push-opt-in.js';

export { esIOS, esStandalone };

const UA = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';

export function esAndroid() {
    return /android/i.test(UA);
}

/**
 * ¿Estamos dentro del navegador interno de una app (WhatsApp/Instagram/Facebook/
 * Messenger/TikTok/Line)? Ahí NO se puede instalar la PWA: hay que mandar al
 * usuario al navegador real. Detección por marcas conocidas en el UA. Es
 * best-effort (UA spoofeable) → el consumidor debe degradar con gracia.
 */
export function esInAppWebview() {
    return (
        /\bWhatsApp\b/i.test(UA) ||
        /\bFBAN\b|\bFBAV\b|\bFB_IAB\b/i.test(UA) || // Facebook / Messenger
        /\bInstagram\b/i.test(UA) ||
        /\bBytedanceWebview\b|\bmusical_ly\b|\bTikTok\b/i.test(UA) || // TikTok
        /\bLine\b/i.test(UA)
    );
}

/**
 * ¿Es Safari REAL en iOS (no Chrome/Edge/Firefox iOS, que usan WebKit pero con
 * otro UA y otro menú de compartir)? Solo Safari real ofrece "Agregar a inicio".
 * Chrome iOS = CriOS, Edge = EdgiOS, Firefox = FxiOS.
 */
export function esSafariIOSReal() {
    if (!esIOS()) return false;
    return !/CriOS|EdgiOS|FxiOS/i.test(UA) && !esInAppWebview();
}

/**
 * Decide el camino de instalación según el entorno. El wizard lo usa para mostrar
 * la pantalla correcta sin árboles de if dispersos.
 *   'in-app'      → estás en WhatsApp/IG/etc → "abrí en Chrome/Safari".
 *   'ios-safari'  → Safari iOS real → guía "Compartir → Agregar a inicio".
 *   'ios-otro'    → Chrome/Firefox iOS → "abrí en Safari para instalar".
 *   'android'     → Android navegador real → prompt nativo (o guía si no llega).
 *   'instalada'   → ya corre standalone → no ofrecer instalar.
 *   'desktop'     → escritorio u otro → instalar opcional, foco en web.
 */
export function caminoDeInstalacion() {
    if (esStandalone()) return 'instalada';
    if (esInAppWebview()) return 'in-app';
    if (esIOS()) return esSafariIOSReal() ? 'ios-safari' : 'ios-otro';
    if (esAndroid()) return 'android';
    return 'desktop';
}

export default {
    esStandalone,
    esIOS,
    esAndroid,
    esInAppWebview,
    esSafariIOSReal,
    caminoDeInstalacion,
};
